import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { pollTask } from "../lib/tasks";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";
import { ArrowLeft, FileText, Sparkle, Quotes, Warning, ListChecks, Receipt, Bank } from "@phosphor-icons/react";

export default function ObservatoryReport() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [taxo, setTaxo] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [debtAnalyzing, setDebtAnalyzing] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/observatory/reports/${id}`).then((res) => setR(res.data)),
      api.get("/observatory/taxonomy").then((res) => setTaxo(res.data)),
    ]).catch(() => toast.error("Erreur chargement"));
  }, [id]);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const resp = await api.post(`/observatory/reports/${id}/analyze`);
      toast.info("Analyse Claude en cours — patientez ~1 min");
      const final = await pollTask(resp.data.task_id, { intervalMs: 2500, timeoutMs: 180000 });
      if (final.status === "error") throw new Error(final.error);
      const upd = await api.get(`/observatory/reports/${id}`);
      setR(upd.data);
      toast.success("Analyse terminée");
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || "Erreur"); }
    finally { setAnalyzing(false); }
  };

  const debtPro = async () => {
    setDebtAnalyzing(true);
    try {
      const resp = await api.post(`/observatory/reports/${id}/debt-pro`);
      toast.info("Analyseur de Dette Pro — détection des passifs occultes...");
      const final = await pollTask(resp.data.task_id, { intervalMs: 2500, timeoutMs: 180000 });
      if (final.status === "error") throw new Error(final.error);
      const upd = await api.get(`/observatory/reports/${id}`);
      setR(upd.data);
      toast.success("Passifs occultes détectés");
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || "Erreur"); }
    finally { setDebtAnalyzing(false); }
  };

  if (!r || !taxo) return <div className="p-12 label-mono">Chargement...</div>;

  const orgInfo = taxo.organisations[r.organisation];
  const paysInfo = taxo.pays.find((p) => p.code === r.pays);

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10">
      <Link to="/observatory" className="label-mono inline-flex items-center gap-2 mb-6 hover:text-[var(--signal)]" data-testid="obs-back">
        <ArrowLeft size={14} /> Retour à la veille
      </Link>

      <div className="border-b border-black pb-6 mb-8 fade-up">
        <div className="flex items-center gap-3 flex-wrap mb-3">
          {orgInfo && (
            <span className="font-mono text-xs px-2 py-1 border" style={{ borderColor: orgInfo.couleur, color: orgInfo.couleur }}>
              {orgInfo.label}
            </span>
          )}
          <span className="font-mono text-xs px-2 py-1 border border-black/30">{r.type_rapport}</span>
          <span className="font-mono text-xs px-2 py-1 border border-black/30">{paysInfo?.nom || r.pays}</span>
          <span className="font-mono text-xs px-2 py-1 border border-black/30">{r.annee}</span>
          <span className="font-mono text-xs px-2 py-1 border border-black/30">{r.cote}</span>
          {r.source_url && <a href={r.source_url} target="_blank" rel="noreferrer" className="font-mono text-xs underline text-[var(--signal)]">Source ↗</a>}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight">{r.titre}</h1>
        <div className="mt-3 flex items-center justify-between">
          <div className="font-mono text-xs text-[var(--muted)]">{r.filename} · {(r.taille/1024).toFixed(0)} Ko · {fmtDate(r.cree_le)}</div>
          {!r.analyse && (
            <button onClick={analyze} disabled={analyzing} data-testid="obs-analyze-btn"
              className="bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] px-4 py-2 label-mono flex items-center gap-2 disabled:opacity-60">
              <Sparkle size={14} weight="fill" /> {analyzing ? "Analyse..." : "Analyser maintenant"}
            </button>
          )}
          {r.analyse && !r.debt_pro && (
            <button onClick={debtPro} disabled={debtAnalyzing} data-testid="obs-debtpro-btn"
              className="border-2 border-[var(--signal)] text-[var(--signal)] hover:bg-[var(--signal)] hover:text-[var(--paper)] px-4 py-2 label-mono flex items-center gap-2 disabled:opacity-60">
              <Bank size={14} weight="fill" /> {debtAnalyzing ? "Détection..." : "Analyseur de Dette Pro"}
            </button>
          )}
        </div>
      </div>

      {/* Workspace split-view : extrait + analyse */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-black/15 border border-black/15">
        {/* Left : extrait du rapport */}
        <section className="bg-[var(--paper)] p-6 max-h-[800px] overflow-auto">
          <div className="flex items-center gap-2 mb-3 sticky top-0 bg-[var(--paper)] py-2 -mt-2">
            <FileText size={18} className="text-[var(--signal)]" />
            <div className="label-mono">§ Document source · extrait</div>
          </div>
          <pre className="font-mono text-xs leading-relaxed text-[var(--ink)] whitespace-pre-wrap">{r.extrait_texte || "—"}</pre>
        </section>

        {/* Right : analyse IA */}
        <section className="bg-[var(--paper)] p-6">
          <div className="label-mono mb-3">§ Analyse IA · multi-niveaux</div>
          {!r.analyse ? (
            <div className="border border-black/15 bg-[var(--paper-2)] p-8 text-center">
              <Sparkle size={32} weight="duotone" className="mx-auto text-[var(--muted)]" />
              <div className="mt-4 font-serif text-xl text-[var(--muted)]">Analyse IA non lancée</div>
              <p className="mt-2 text-sm text-[var(--muted)]">Cliquez sur « Analyser maintenant » pour produire un résumé exécutif, extraire les ratios et identifier les alertes.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {r.analyse.executive_summary && (
                <Block icon={Receipt} title="Executive summary">
                  <p className="font-serif text-lg leading-relaxed">{r.analyse.executive_summary}</p>
                </Block>
              )}
              {r.analyse.enjeux_finances_publiques?.length > 0 && (
                <Block icon={ListChecks} title="Enjeux finances publiques">
                  <ul className="space-y-2">
                    {r.analyse.enjeux_finances_publiques.map((e, i) => (
                      <li key={i} className="flex gap-3"><span className="font-mono text-xs text-[var(--signal)] mt-1">{String(i+1).padStart(2,"0")}</span><span>{e}</span></li>
                    ))}
                  </ul>
                </Block>
              )}
              {r.analyse.analyse_impact && (
                <Block icon={Receipt} title="Analyse d'impact (budget national)">
                  <p className="leading-relaxed">{r.analyse.analyse_impact}</p>
                </Block>
              )}
              {r.analyse.ratios && (
                <Block icon={Receipt} title="Ratios extraits">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-black/15 border border-black/15">
                    {Object.entries(r.analyse.ratios).map(([k, v]) => (
                      <div key={k} className="bg-[var(--paper)] p-3">
                        <div className="label-mono text-[9px] capitalize">{k.replace(/_/g, " ")}</div>
                        <div className="mt-1 text-xl font-light tabular-nums">{Number(v).toFixed(1)}<span className="text-xs text-[var(--muted)] ml-1">%</span></div>
                      </div>
                    ))}
                  </div>
                </Block>
              )}
              {r.analyse.recommandations?.length > 0 && (
                <Block icon={ListChecks} title="Recommandations politiques">
                  <ul className="space-y-2 list-disc pl-5 text-sm">
                    {r.analyse.recommandations.map((rc, i) => <li key={i}>{rc}</li>)}
                  </ul>
                </Block>
              )}
              {r.analyse.alertes?.length > 0 && (
                <Block icon={Warning} title="Alertes risques" accent>
                  <ul className="space-y-2">
                    {r.analyse.alertes.map((a, i) => (
                      <li key={i} className="flex gap-2 text-[var(--signal-hover)]"><Warning size={16} weight="fill" className="shrink-0 mt-0.5" /><span>{a}</span></li>
                    ))}
                  </ul>
                </Block>
              )}
              {r.analyse.citations?.length > 0 && (
                <Block icon={Quotes} title="Citations · traçabilité">
                  <div className="space-y-3">
                    {r.analyse.citations.map((c, i) => (
                      <blockquote key={i} className="border-l-2 border-[var(--signal)] pl-4 py-1">
                        <p className="font-serif italic text-sm">« {c.quote} »</p>
                        {c.page > 0 && <div className="mt-1 font-mono text-xs text-[var(--muted)]">p. {c.page}</div>}
                      </blockquote>
                    ))}
                  </div>
                </Block>
              )}

              {r.debt_pro && (
                <DebtProBlock data={r.debt_pro} />
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function DebtProBlock({ data }) {
  const passifs = data.passifs_occultes || [];
  return (
    <div className="border-2 border-[var(--signal)] bg-[var(--signal-light)] p-5" data-testid="debt-pro-block">
      <div className="flex items-center gap-2 mb-3">
        <Bank size={18} weight="fill" className="text-[var(--signal)]" />
        <div className="label-mono text-[var(--signal-hover)]">Analyseur de Dette Pro · Passifs occultes</div>
      </div>
      {data.synthese && (
        <p className="font-serif text-base leading-relaxed mb-4">{data.synthese}</p>
      )}
      {data.ratio_occulte_sur_dette_officielle_pct > 0 && (
        <div className="mb-4 inline-block stamp-rotate border-2 border-[var(--signal)] text-[var(--signal)] px-3 py-1.5 font-mono text-sm">
          OCCULTE / OFFICIEL : {Number(data.ratio_occulte_sur_dette_officielle_pct).toFixed(1)}%
        </div>
      )}
      {passifs.length > 0 ? (
        <div className="space-y-3">
          {passifs.map((p, i) => (
            <div key={i} className="border border-black/20 bg-[var(--paper)] p-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <span className="font-mono text-xs px-2 py-0.5 border border-[var(--signal)] text-[var(--signal)]">{p.categorie}</span>
                  <div className="mt-2 font-serif text-lg">{p.libelle}</div>
                  {p.beneficiaire && <div className="text-xs text-[var(--muted)] mt-1">→ {p.beneficiaire}</div>}
                </div>
                {p.montant_estime > 0 && (
                  <div className="text-right">
                    <div className="text-2xl font-light tabular-nums">{Number(p.montant_estime).toLocaleString("fr-FR")}</div>
                    <div className="font-mono text-xs text-[var(--muted)]">{p.devise} · {p.horizon}</div>
                  </div>
                )}
              </div>
              {p.impact_vitaliste && <p className="mt-3 text-sm italic text-[var(--ink)]">{p.impact_vitaliste}</p>}
              {p.citation?.quote && (
                <blockquote className="mt-3 border-l-2 border-[var(--signal)] pl-3 text-xs font-serif italic text-[var(--muted)]">
                  « {p.citation.quote} » {p.citation.page > 0 && <span className="font-mono">— p. {p.citation.page}</span>}
                </blockquote>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm italic">Aucun passif occulte détecté dans ce document.</p>
      )}
      {data.alertes_majeures?.length > 0 && (
        <div className="mt-4 border-t border-[var(--signal)] pt-3">
          <div className="label-mono text-[var(--signal-hover)]">Alertes majeures</div>
          <ul className="mt-2 space-y-1">
            {data.alertes_majeures.map((a, i) => (
              <li key={i} className="flex gap-2 text-sm"><Warning size={14} weight="fill" className="text-[var(--signal)] shrink-0 mt-0.5" /><span>{a}</span></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Block({ icon: Icon, title, children, accent }) {
  return (
    <div className={`border ${accent ? "border-[var(--signal)] bg-[var(--signal-light)]" : "border-black/15"} p-4`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className={accent ? "text-[var(--signal)]" : "text-[var(--ink)]"} />
        <div className="label-mono">{title}</div>
      </div>
      {children}
    </div>
  );
}
